import { db } from '@/db/client';
import type { UserId } from '@/schemas';

export interface SessionSyncState {
  id?: string;
  sessionId: string;
  lastUploadedAt?: number;
  lastDownloadedAt?: number;
}

export interface UserSyncState {
  id?: string;
  sessionId: string; // Composite key: `${userId}:${entityType}`
  userId: string;
  entityType:
    | 'player_events'
    | 'game_definitions'
    | 'team_presets'
    | 'player_groups'
    | 'session_index'
    | 'images';
  lastUploadedAt?: number;
  lastDownloadedAt?: number;
}

export async function getSyncState(sessionId: string): Promise<SessionSyncState> {
  const existing = await db.syncState.get(sessionId);
  if (existing) {
    return existing as SessionSyncState;
  }
  return { id: sessionId, sessionId };
}

export async function setLastUploadedAt(sessionId: string, ts: number): Promise<void> {
  const existing = await getSyncState(sessionId);
  await db.syncState.put({ ...existing, id: sessionId, sessionId, lastUploadedAt: ts });
}

export async function setLastDownloadedAt(sessionId: string, ts: number): Promise<void> {
  const existing = await getSyncState(sessionId);
  await db.syncState.put({ ...existing, id: sessionId, sessionId, lastDownloadedAt: ts });
}

function getUserSyncStateKey(userId: UserId, entityType: UserSyncState['entityType']): string {
  return `${String(userId)}:${entityType}`;
}

export async function getUserSyncState(
  userId: UserId,
  entityType: UserSyncState['entityType'],
): Promise<UserSyncState> {
  if (!userId) {
    throw new Error('userId is required for getUserSyncState');
  }
  const key = getUserSyncStateKey(userId, entityType);
  if (!key || key === ':' || key.length === 0) {
    throw new Error(
      `Failed to generate sync state key. userId: ${String(userId)}, entityType: ${entityType}, key: ${key}`,
    );
  }

  const existing = await db.syncState.get(key);
  if (existing && existing.userId && existing.entityType) {
    const typed = existing as UserSyncState;
    return { ...typed, id: typed.id ?? key, sessionId: key };
  }

  const state: UserSyncState = {
    id: key,
    sessionId: key,
    userId: String(existing?.userId ?? userId),
    entityType: (existing?.entityType as UserSyncState['entityType']) ?? entityType,
    lastUploadedAt: existing?.lastUploadedAt,
    lastDownloadedAt: existing?.lastDownloadedAt,
  };
  await db.syncState.put(state);
  return state;
}

export async function setUserLastUploadedAt(
  userId: UserId,
  entityType: UserSyncState['entityType'],
  ts: number,
): Promise<void> {
  const key = getUserSyncStateKey(userId, entityType);
  const existing = await getUserSyncState(userId, entityType);
  const state: UserSyncState = {
    id: key,
    sessionId: key,
    userId: existing.userId,
    entityType,
    lastUploadedAt: ts,
    lastDownloadedAt: existing.lastDownloadedAt,
  };
  await db.syncState.put(state);
}

export async function setUserLastDownloadedAt(
  userId: UserId,
  entityType: UserSyncState['entityType'],
  ts: number,
): Promise<void> {
  const key = getUserSyncStateKey(userId, entityType);
  const existing = await getUserSyncState(userId, entityType);
  const state: UserSyncState = {
    id: key,
    sessionId: key,
    userId: existing.userId,
    entityType,
    lastUploadedAt: existing.lastUploadedAt,
    lastDownloadedAt: ts,
  };
  await db.syncState.put(state);
}
